import { Description, Delete } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { CreatePatientForm } from "types";

interface ConsentFormUploadProps {
  name: keyof CreatePatientForm;
  title: string;
  description: string;
  error?: string;
}

function ConsentFormUpload({
  name,
  title,
  description,
  error,
}: ConsentFormUploadProps) {
  const { control } = useFormContext<CreatePatientForm>();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    onChange: (file: File | null) => void
  ) => {
    const file = event.target.files?.[0] || null;

    if (file) {
      setUploadedFile(file);
      onChange(file);
    }
  };

  const handleRemoveFile = (onChange: (file: File | null) => void) => {
    setUploadedFile(null);
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange } }) => (
        <Card
          variant="outlined"
          className={`border ${error ? "border-red-500" : "border-gray-300"} rounded-2xl hover:border-blue-300 transition-colors`}
        >
          <CardContent className="p-6">
            <Box className="flex items-center justify-between">
              <Box className="flex-1">
                <Typography variant="h6" className="text-lg font-medium mb-2">
                  {title}
                </Typography>
                <Typography variant="body2" className="text-gray-600">
                  {description}
                </Typography>

                {uploadedFile && (
                  <Box className="flex items-center gap-2 mt-3">
                    <Description className="text-green-600" />
                    <Box>
                      <Typography
                        variant="body2"
                        className="font-medium text-green-700"
                      >
                        {uploadedFile.name}
                      </Typography>
                      <Typography variant="caption" className="text-gray-500">
                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFile(onChange)}
                      className="text-red-500 hover:text-red-700 ml-2"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                )}

                {error && (
                  <Typography
                    variant="caption"
                    className="text-red-500 mt-2 block"
                  >
                    {error}
                  </Typography>
                )}
              </Box>

              <Box className="ml-6">
                <Button
                  variant="outlined"
                  onClick={handleClick}
                  className="rounded-full px-6 py-2 border-gray-300 text-gray-700 hover:border-blue-400 hover:text-blue-600"
                  sx={{
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  Upload File
                </Button>
              </Box>
            </Box>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              style={{ display: "none" }}
              onChange={(e) => handleFileChange(e, onChange)}
            />
          </CardContent>
        </Card>
      )}
    />
  );
}

export default ConsentFormUpload;
